<template>
    <div>
        <v-row>
            <v-col sm="12" md="6" lg="3">
                <v-card elevation="5">
                    <v-card-text v-if="fetchingQuota">
                        <dashboard-loader></dashboard-loader>
                    </v-card-text>
                    <v-card-text v-else-if="error">
                        <dashboard-error :msg="errorMsg" />
                    </v-card-text>
                    <template v-else-if="hasCurrentQuota">
                        <v-card-title>
                            <div class="secondary--text">Current credit quota</div>
                        </v-card-title>
                        <v-card-subtitle>
                            End date:
                            <b v-if="quota.end_date">{{ quota.end_date | dateToHuman }}</b>
                            <b v-else>not set</b>
                        </v-card-subtitle>

                        <v-card-text class="text-center">
                            <div class="text-h4 secondary--text py-6">{{ formatAC(quota.limit) }}</div>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <instance-credit-quota-edit-dialog @success="fetchAll()" :quota="quota || {}">
                                <v-btn text color="primary">Edit</v-btn>
                            </instance-credit-quota-edit-dialog>
                        </v-card-actions>
                    </template>
                    <template v-else-if="isQuotaCreateState">
                        <v-card-title>
                            <div class="secondary--text">Current credit quota</div>
                        </v-card-title>
                        <v-card-text class="d-flex flex-column align-center text-center pt-4 pb-10">
                            <div class="secondary--text mb-4">
                                You don't have a credit quota set yet.
                                <br />
                                Let's create your first quota!
                            </div>

                            <instance-credit-quota-edit-dialog crud="create" @success="fetchAll()" :quota="{}">
                                <v-btn color="primary">
                                    <v-icon left>add</v-icon>
                                    Quota
                                </v-btn>
                            </instance-credit-quota-edit-dialog>
                        </v-card-text>
                    </template>
                    <v-card-text v-else>
                        <dashboard-empty>This subscription has no limits set</dashboard-empty>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col sm="12" md="12" lg="12">
                <instance-credit-quota-schedule-table />
            </v-col>
            <v-col sm="12" md="12" lg="12">
                <v-card flat>
                    <v-card-title>
                        <div class="secondary--text">Usage across instances</div>
                    </v-card-title>
                    <v-card-subtitle>Usage is compared to the current credit quota</v-card-subtitle>
                    <v-card-text v-if="fetchingUsages">
                        <dashboard-loader></dashboard-loader>
                    </v-card-text>
                    <v-card-text v-else-if="usagesError || error">
                        <dashboard-error :msg="usagesErrorMsg || errorMsg" />
                    </v-card-text>
                    <v-card-text v-else>
                        <div class="d-flex align-center">
                            <v-spacer></v-spacer>
                            <v-text-field
                                v-model="instanceSearch"
                                prepend-inner-icon="mdi-magnify "
                                hide-details
                                solo
                                flat
                                background-color="grey lighten-4"
                                dense
                                placeholder="Search for an instance..."
                                clearable
                                @click:clear="instanceSearch = ''"></v-text-field>
                        </div>
                        <v-data-table
                            item-key="end_date"
                            :headers="scheduleHeaders"
                            :items="usages"
                            :search="instanceSearch"
                            :options="tableOptions"
                            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                            class="pb-12">
                            <template v-slot:top="{ pagination, options, updateOptions }">
                                <div class="d-flex align-center">
                                    <v-spacer></v-spacer>
                                    <v-data-footer
                                        :pagination="pagination"
                                        :options="options"
                                        @update:options="updateOptions"
                                        :itemsPerPageOptions="itemsPerPageOptions"
                                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                        class="no-border" />
                                </div>
                            </template>
                            <template v-slot:no-data>
                                <span>No instance usages found.</span>
                            </template>

                            <template v-slot:[`item.long_id`]="{ item }">
                                <b>{{ item.long_id }}</b>
                            </template>

                            <template v-slot:[`item.usage`]="{ item }">
                                <v-progress-linear
                                    :value="item.usage_pct"
                                    :color="usageColor(item.usage_pct)"
                                    rounded
                                    class="my-2"
                                    height="8"></v-progress-linear>
                                <div class="d-flex">
                                    <span>{{ item.usage_pct }}% used</span>
                                    <v-spacer></v-spacer>
                                    <strong>{{ formatAC(item.usage) }}</strong>
                                    /
                                    <span>{{ formatAC(quota.limit) }}</span>
                                </div>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import fileQuotas from '@/mixins/fileQuotas'
import { toLocaleFixed } from '@/utils'
import { mapActions, mapState } from 'vuex'
const DashboardError = () => import('@/components/DashboardError.vue')
const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const DashboardLoader = () => import('@/components/DashboardLoader.vue')
const InstanceCreditQuotaEditDialog = () => import('@/modules/space/components/InstanceCreditQuotaEditDialog.vue')
const InstanceCreditQuotaScheduleTable = () => import('@/modules/space/components/InstanceCreditQuotaScheduleTable.vue')

export default {
    name: 'StudentQuotas',
    mixins: [fileQuotas],
    components: {
        DashboardError,
        DashboardEmpty,
        DashboardLoader,
        InstanceCreditQuotaEditDialog,
        InstanceCreditQuotaScheduleTable
    },
    data() {
        return {
            precision: this.$appConfig?.VUE_APP_DASHBOARD_PRECISION,
            instanceSearch: '',
            scheduleHeaders: [
                { text: 'Instance', align: 'left', value: 'long_id' },
                { text: 'Usage', align: 'left', value: 'usage' }
            ],
            itemsPerPageOptions: [5, 20, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 5,
                pageStart: 1,
                pageStop: 5,
                itemsLength: 5
            }
        }
    },
    created() {
        this.refreshStudentQuotaData(this.$route.params.sid)
    },
    methods: {
        ...mapActions('spaceStore', ['refreshStudentQuotaData']),
        formatAC(value, precision = this.precision) {
            return toLocaleFixed(value, precision)
        },
        fetchAll() {
            this.refreshStudentQuotaData(this.$route.params.sid)
        }
    },
    computed: {
        ...mapState('spaceStore', {
            quota: state => state.studentQuotaCurrent,
            fetchingQuota: state => state.studentQuotaFetchingCurrent,
            error: state => state.studentQuotaCurrentError,
            errorMsg: state => state.studentQuotaCurrentErrorMsg,
            usages: state => state.studentQuotaUsages,
            fetchingUsages: state => state.studentQuotaFetchingUsages,
            usagesError: state => state.studentQuotaUsagesError,
            usagesErrorMsg: state => state.studentQuotaUsagesErrorMsg
        }),
        isQuotaCreateState() {
            return Boolean(this.quota) && Number(this.quota.sclid) === 0
        },
        hasCurrentQuota() {
            return Boolean(this.quota) && !this.isQuotaCreateState
        }
    }
}
</script>
